import type { WbsTreeNode, FlatWbsRow } from './types';

/** WBS 트리를 펼침 상태 기준으로 평탄화 */
export function flattenWbsTree(
  nodes: WbsTreeNode[],
  expandedIds: Set<string>,
): FlatWbsRow[] {
  const result: FlatWbsRow[] = [];

  function walk(items: WbsTreeNode[]) {
    for (const node of items) {
      const hasChildren = (node.children?.length ?? 0) > 0;
      const isExpanded = expandedIds.has(node.wbsItemId);
      result.push({ node, isExpanded, hasChildren });
      if (isExpanded && hasChildren) {
        walk(node.children);
      }
    }
  }

  walk(nodes);
  return result;
}

/** 날짜를 M/d 형식으로 포맷 */
export function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** 날짜를 YY. MM. DD 형식으로 포맷 (REQ-V27-003) */
export function formatYYMMDD(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}. ${mm}. ${dd}`;
}

/** 날짜를 YYYY-MM-DD 형식으로 포맷 (input[type=date]용) */
export function toInputDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Gantt 차트 날짜 범위 계산 */
export function getDateRange(
  nodes: WbsTreeNode[],
): { minDate: Date; maxDate: Date } | null {
  let min = Infinity;
  let max = -Infinity;

  function walk(items: WbsTreeNode[]) {
    for (const n of items) {
      if (n.startDate) min = Math.min(min, new Date(n.startDate).getTime());
      if (n.endDate) max = Math.max(max, new Date(n.endDate).getTime());
      if (n.computed?.expectedDelay) {
        max = Math.max(max, new Date(n.computed.expectedDelay).getTime());
      }
      if (n.children) walk(n.children);
    }
  }

  walk(nodes);
  if (min === Infinity || max === -Infinity) return null;

  // 양쪽 여유 7일
  const minDate = new Date(min);
  minDate.setDate(minDate.getDate() - 7);
  const maxDate = new Date(max);
  maxDate.setDate(maxDate.getDate() + 14);
  return { minDate, maxDate };
}

/** 날짜를 차트 x좌표로 변환 */
export function dateToX(
  date: Date,
  minDate: Date,
  dayWidth: number,
): number {
  const diffMs = date.getTime() - minDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays * dayWidth;
}

/** REQ-V27-007: depth별 행 배경색 */
export const DEPTH_ROW_BG: Record<number, string> = {
  1: 'bg-gray-200',
  2: 'bg-gray-100',
  3: 'bg-gray-50',
};

export const DEPTH_ROW_HOVER: Record<number, string> = {
  1: 'bg-gray-300/50',
  2: 'bg-gray-200/50',
  3: 'bg-blue-50/30',
};
